import React, { useState, useEffect } from 'react';
import SVG from 'react-inlinesvg';
import { useFlags } from 'launchdarkly-react-client-sdk';
import SolarPanel from '/images/advocacy/solar-panel.svg';
import SolarPanels from '/images/advocacy/solar-panels2.png';
import { copyFor } from 'config';
import { HAS_ADVOCACY_SUBMISSION } from 'pages/solar/states/with-utility-accounts/active/active.api';
import { useQuery } from '@apollo/client';
import { VoiceSupportCardWaitlist } from 'pages/solar/components/voice-support-card-waitlist';
import { AdvocacyShare } from 'pages/solar/components/advocacy-share';
import { useAccountContext } from 'contexts/account';
import { ErrorPage } from 'components/errors';
import { StyledImage } from './advocacy-dashboard.style';
import {
  GET_ACCOUNT_UTILITY_ACCOUNTS,
  GET_ADVOCACY_CAMPAIGN_DASHBOARD,
} from './advocacy-dashboard.api';

const getCompletedCopy = copyFor('solar.completedCampaignCard');

interface Props {
  userId: number;
  margin?: Margin;
}

export const AdvocacyDashboard = ({ userId, margin }: Props) => {
  const { selectedAccountId } = useAccountContext();
  const [utilityAccountId, setUtilityAccountId] = useState<any>();
  const [state, setState] = useState<any>();
  const [stateAbbreviation, setStateAbbreviation] = useState<any>();
  const [advocacyCampaign, setAdvocacyCampaign] = useState<any>();
  const [
    mostRecentCompletedAdvocacyCampaign,
    setmostRecentCompletedAdvocacyCampaign,
  ] = useState<any>();
  const {
    data: utilityAccountData,
    loading: utilityAccountLoading,
    error: utilityAccountError,
  } = useQuery<
    GetAccountUtilityAccountQuery,
    GetAccountUtilityAccountQueryVariables
  >(GET_ACCOUNT_UTILITY_ACCOUNTS, {
    skip: !selectedAccountId,
    variables: { accountId: selectedAccountId },
  });
  const {
    data: advocacyData,
    loading: advocacyDataLoading,
    error: advocacyDataError,
  } = useQuery<
    GetAdvocacyCampaignDashboardQuery,
    GetAdvocacyCampaignQueryVariables
  >(GET_ADVOCACY_CAMPAIGN_DASHBOARD, {
    skip: !stateAbbreviation,
    variables: { stateAbbreviation },
  });
  const submissionData = useQuery<
    HasAdvocacyCampaignSubmissionQuery,
    HasAdvocacyCampaignSubmissionQueryVariables
  >(HAS_ADVOCACY_SUBMISSION, {
    skip: !advocacyCampaign,
    variables: { advocacyCampaignId: advocacyCampaign?.id || -1, userId },
  });
  const shareText = advocacyCampaign?.shareText;
  const hasAdvocacyCampaignSubmission =
    submissionData?.data?.hasAdvocacyCampaignSubmission;
  const numberOfSubmissions = advocacyCampaign?.numberOfSubmissions;
  const flag = useFlags();

  useEffect(() => {
    if (!utilityAccountLoading && !utilityAccountError) {
      setUtilityAccountId(utilityAccountData?.account?.utilityAccounts[0].id);
      const { name, abbreviation } =
        utilityAccountData?.account?.utilityAccounts[0]?.utility.state || {};
      setState(name);
      setStateAbbreviation(abbreviation);
    }
  }, [utilityAccountLoading, utilityAccountData]);

  useEffect(() => {
    if (!advocacyDataLoading && !advocacyDataError) {
      setAdvocacyCampaign(advocacyData?.state?.activeAdvocacyCampaign);
      setmostRecentCompletedAdvocacyCampaign(
        advocacyData?.state.mostRecentCompletedAdvocacyCampaign
      );
    }
  }, [advocacyDataLoading, advocacyData]);

  if (advocacyDataError || utilityAccountError) {
    return <ErrorPage />;
  }
  // If the most recent non-default campaign is completed, show the completed card
  else if (mostRecentCompletedAdvocacyCampaign) {
    const { message, title } = mostRecentCompletedAdvocacyCampaign;
    const description = getCompletedCopy('description', { message });

    return (
      <AdvocacyShare
        description={description}
        icon={<SVG src={SolarPanel} height="58px" width="100px" />}
        margin={margin}
        shareCopy={getCompletedCopy('shareCopy')}
        socialShareCopy={getCompletedCopy('socialShareCopy')}
        title={title}
      />
    );
  }
  // If the most recent campaign isn't completed, and the user has submitted, show the submission card
  else if (hasAdvocacyCampaignSubmission) {
    const getSubmissionCopy = copyFor('solar.submissionCard.default');

    const description =
      numberOfSubmissions > 9
        ? getSubmissionCopy('description.over9', {
            numberOfSubmissions,
            stateAbbreviation,
            stateFullName: state,
          })
        : getSubmissionCopy('description.under10');

    const icon = <StyledImage src={SolarPanels} />;
    return (
      <AdvocacyShare
        description={description}
        icon={icon}
        margin={margin}
        shareCopy={getSubmissionCopy('shareCopy', {
          stateAbbreviation,
        })}
        socialShareCopy={shareText}
        title={getSubmissionCopy('title')}
      />
    );
  }
  // There is an active campaign, show the voice support card
  return advocacyCampaign?.id && flag.lbShowAdvocacyCampaign ? (
    <VoiceSupportCardWaitlist
      state={state}
      utilityAccountId={utilityAccountId}
    />
  ) : null;
};
